import * as React from 'react';

import { graphql, Link, PageProps } from 'gatsby';
import Layout from 'core/components/Layout';
import SEO from 'core/components/SEO';
import BlogEntryHero from 'blog/components/BlogEntryHero';
import { BlogEntry, BlogListEntry } from 'blog/lib/types';
import BlogContent from 'blog/components/BlogContent';
import RelatedArticles from 'blog/components/RelatedArticles';
import CtaFooter from 'blocks/layouts/CtaFooter';
import { OgImageQuery, PageContextWithMetaData, PageImages } from 'lib/types';
import * as heroStyles from 'blog/components/BlogEntryHero/BlogEntryHero.module.scss';
import BlogAuthor from 'blog/components/BlogAuthor';
import ImageWrapper from 'core/components/ImageWrapper';
import RichSnippets from 'locations/components/RichSnippets';
import * as styles from './blog-entry.module.scss';

type BlogEntryData = OgImageQuery & {
    blogEntry: BlogEntry & {
        alt: string;
        imageWithTextImages: PageImages;
        imageWithTextImagesFull: PageImages;
    };
    related?: {
        nodes: ReadonlyArray<BlogListEntry>;
    };
    expertsPage: {
        pageData: {
            showPage: boolean;
        };
    };
};

export default function BlogEntryTemplate({
    data: { blogEntry, related, ogImage, expertsPage },
    location: { pathname },
    pageContext,
}: PageProps<BlogEntryData, PageContextWithMetaData>) {
    const showWrittenBy = blogEntry.byMedicalExperts || blogEntry.author;
    const linkToExpertsPage = expertsPage?.pageData?.showPage;

    return (
        <Layout
            mainClasses={{ root: styles.root }}
            customAdbar={blogEntry.adbars}
            withFooterTopMargin={false}
        >
            <SEO
                title={pageContext.title || blogEntry.title}
                pathname={pathname}
                description={blogEntry.metaDescription}
                ogImage={ogImage}
            />
            <BlogEntryHero
                title={blogEntry.title}
                image={
                    <ImageWrapper
                        image={blogEntry.image?.file}
                        className={heroStyles.image}
                        alt={blogEntry.alt ?? 'Blog Hero Image'}
                    />
                }
                date={blogEntry.date}
                writtenBy={showWrittenBy && <span>{blogEntry.writtenBy}</span>}
                author={
                    showWrittenBy ? (
                        linkToExpertsPage ? (
                            <Link
                                to="/experts"
                                className={styles.medicalExpertsLink}
                            >
                                {blogEntry.author?.teamMember?.name ??
                                    'Medical Experts'}
                            </Link>
                        ) : (
                            <div>
                                {blogEntry.author?.teamMember?.name ??
                                    'Medical Experts'}
                            </div>
                        )
                    ) : undefined
                }
            />
            <BlogContent entry={blogEntry} />
            {blogEntry.author?.teamMember && (
                <BlogAuthor author={blogEntry.author.teamMember} />
            )}
            {related && related.nodes.length > 0 && (
                <RelatedArticles entries={related.nodes} />
            )}
            <CtaFooter />

            <RichSnippets
                data={blogEntry}
                slug={pathname}
                schemaType="Article"
            />
        </Layout>
    );
}

export const blogEntryQuery = graphql`
    fragment BlogImageWithText on BlogEntry {
        imageWithTextImages: pageImages {
            slug
            alt
            image {
                mobile: file {
                    publicURL
                    alt
                    childImageSharp {
                        gatsbyImageData(
                            aspectRatio: 1.934
                            transformOptions: {
                                fit: COVER
                                cropFocus: ATTENTION
                            }
                            sizes: "calc(100vw - 40px)"
                        )
                    }
                }
                desktop: file {
                    publicURL
                    alt
                    childImageSharp {
                        gatsbyImageData(
                            aspectRatio: 1.968
                            transformOptions: {
                                fit: COVER
                                cropFocus: ATTENTION
                            }
                            layout: FULL_WIDTH
                        )
                    }
                }
            }
        }
        imageWithTextImagesFull: pageImages {
            slug
            alt
            image {
                mobile: file {
                    publicURL
                    alt
                    childImageSharp {
                        gatsbyImageData(
                            aspectRatio: 2.8
                            transformOptions: {
                                fit: COVER
                                cropFocus: ATTENTION
                            }
                            sizes: "calc(100vw - 40px)"
                        )
                    }
                }
                desktop: file {
                    publicURL
                    alt
                    childImageSharp {
                        gatsbyImageData(
                            height: 330
                            aspectRatio: 3.433
                            transformOptions: {
                                fit: COVER
                                cropFocus: ATTENTION
                            }
                            layout: FULL_WIDTH
                        )
                    }
                }
            }
        }
    }

    query BlogEntryQuery(
        $slug: String!
        $filter: BlogEntryFilterInput!
        $ogImage: String
    ) {
        blogEntry(slug: { eq: $slug }) {
            id
            alt
            title
            date
            metaDescription
            content {
                data
                blockType
            }
            byMedicalExperts
            writtenBy
            author {
                teamMember {
                    ...BlogAuthor
                }
            }
            adbars {
                title
                startTime
                endTime
            }
            ...BlogImageWithText
            image: featuredImage {
                file {
                    publicURL
                    alt
                    mobile: childImageSharp {
                        gatsbyImageData(
                            width: 414
                            height: 290
                            sizes: "(max-width: 414px) 100vw, 414px"
                            transformOptions: { fit: COVER, cropFocus: ENTROPY }
                        )
                    }
                    desktop: childImageSharp {
                        gatsbyImageData(
                            width: 600
                            height: 661
                            transformOptions: { fit: COVER, cropFocus: ENTROPY }
                            sizes: "600px"
                        )
                    }
                }
            }
        }
        related: allBlogEntry(filter: $filter, limit: 3, sort: { date: DESC }) {
            nodes {
                ...BlogCardData
            }
        }
        ogImage: upload(imageId: { eq: $ogImage }) {
            ...OgImage
        }
        expertsPage: page(slug: { eq: "experts" }) {
            pageData
        }
    }
`;
